import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, fmtErr } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Truck, CheckCircle2, ShieldCheck } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, valid: false, user: null, error: "" });
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setState({ loading: false, valid: false, error: "Missing reset token" }); return; }
    api.get(`/auth/reset-token/${token}`)
      .then((r) => setState({ loading: false, valid: true, user: r.data, error: "" }))
      .catch((e) => setState({ loading: false, valid: false, error: fmtErr(e.response?.data?.detail) || "Invalid link" }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw !== pw2) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Password updated");
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-gradient-to-br from-slate-900 via-blue-900 to-blue-700 text-white">
        <div className="flex items-center gap-2.5">
          <div className="h-10 w-10 rounded-lg bg-white/10 backdrop-blur border border-white/20 grid place-items-center">
            <Truck className="h-5 w-5" />
          </div>
          <div className="font-heading font-bold text-lg">Dispatch Register</div>
        </div>
        <div className="max-w-md">
          <h1 className="font-heading text-4xl xl:text-5xl font-bold leading-tight">Reset your password</h1>
          <p className="mt-4 text-blue-100/90 text-lg">Choose a new password to regain access. This link works only once and expires in 24 hours.</p>
          <div className="mt-8 flex items-center gap-3 text-blue-100/90 text-sm">
            <ShieldCheck className="h-5 w-5" /> One-time use, admin-issued
          </div>
        </div>
        <div className="text-xs text-blue-200/70">© {new Date().getFullYear()} Dispatch Register</div>
      </div>
      <div className="flex items-center justify-center p-6">
        <Card className="w-full max-w-md shadow-lg border-slate-200">
          <CardContent className="p-8">
            {state.loading && <div className="text-slate-500 text-sm">Validating link…</div>}
            {!state.loading && !state.valid && (
              <div className="space-y-4">
                <h2 className="font-heading text-2xl font-bold text-slate-900">Link unavailable</h2>
                <p className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2" data-testid="reset-error">{state.error}</p>
                <p className="text-sm text-slate-500">Ask your admin to generate a fresh reset link from the Users screen.</p>
                <Link to="/login" className="text-blue-600 text-sm underline">Back to sign in</Link>
              </div>
            )}
            {!state.loading && state.valid && !done && (
              <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
                <div>
                  <h2 className="font-heading text-2xl font-bold text-slate-900">Set a new password</h2>
                  <p className="text-sm text-slate-500 mt-1">
                    For <b>{state.user?.name}</b> · User ID <span className="font-mono-num">{state.user?.user_id}</span>
                  </p>
                </div>
                <div>
                  <Label>New Password</Label>
                  <Input type="password" required value={pw} onChange={(e) => setPw(e.target.value)}
                    data-testid="reset-pw1" placeholder="At least 6 characters" />
                </div>
                <div>
                  <Label>Confirm Password</Label>
                  <Input type="password" required value={pw2} onChange={(e) => setPw2(e.target.value)}
                    data-testid="reset-pw2" />
                </div>
                <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={busy} data-testid="reset-submit">
                  {busy ? "Updating…" : "Update password"}
                </Button>
              </form>
            )}
            {done && (
              <div className="space-y-4 text-center py-6" data-testid="reset-done">
                <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto" />
                <h2 className="font-heading text-xl font-bold text-slate-900">Password updated</h2>
                <p className="text-sm text-slate-500">You can now sign in with your new password.</p>
                <Button onClick={() => navigate("/login")} className="bg-blue-600 hover:bg-blue-700" data-testid="reset-go-login">Go to sign in</Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
